/**
 * aeterna/neural/crossmodal.ts — Room↔Text Binding
 *
 * The Aeterna processes text in one system and the room exists separately.
 * This bilinear cross-modal binding layer creates a shared metric space
 * where you can measure *coherence between what the player says and
 * what the room contains*.
 *
 * Two projection matrices map text embeddings and room-state vectors
 * into a shared 32-dim space. Trained via online contrastive learning:
 * messages typed WHILE looking at furniture should be close to that
 * furniture's encoding. Messages about "cozy" should be close to
 * rooms with high warmth.
 *
 * The Aeterna can now say "you've been talking about wanting peace
 * but your room is full of arcade machines" — not from a rule,
 * from geometric incoherence between the two modalities.
 *
 * Also enables: "the room feels different since you started talking
 * about your childhood" — because the text-projected vector drifted
 * away from the room-projected vector in the shared space.
 */

import {
  zeros, matvec, normalize, cosine, vadd, vsub, vscale,
  randomMatrix, mulberry32, gaussianSeeded, dot, norm, ema,
  f32ToBase64, base64ToF32,
} from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Shared embedding dimensionality */
const SHARED_DIM = 32;

/** Text embedding input dim (from embeddings.ts) */
const TEXT_DIM = 64;

/** Room state input dim (mood dimensions + furniture features) */
const ROOM_DIM = 32;

/** Affect vector dim (from CharCNN) */
const AFFECT_DIM = 8;

/** Personality dim (from Liquid ODE: patience, curiosity, warmth, wit) */
const PERSONALITY_DIM = 4;

/** Drive dim (from HomeostaticDriveSystem: 5 drives) */
const DRIVE_DIM = 5;

/** Contrastive learning margin */
const CONTRASTIVE_MARGIN = 0.3;

/** Online learning rate */
const LEARN_RATE = 0.005;

/** Temperature for contrastive softmax */
const TEMPERATURE = 0.07;

// ─── Room Feature Encoder ────────────────────────────────────────────────────

export interface RoomFeatures {
  /** Mood dimensions: warmth, energy, mystery, nature, noise (0-1 each) */
  warmth: number;
  energy: number;
  mystery: number;
  nature: number;
  noise: number;
  /** Furniture category counts (normalized) */
  seatingCount: number;
  lightingCount: number;
  decorCount: number;
  storageCount: number;
  natureCount: number;
  entertainmentCount: number;
  /** Spatial features */
  furnitureDensity: number;    // 0-1: how full is the room
  symmetry: number;            // 0-1: how symmetrically arranged
  centeredness: number;        // 0-1: furniture clustered at center vs edges
  /** Temporal */
  timeOfDay: number;           // 0-1: normalized hour
  seasonPhase: number;         // 0-1: season progress
  /** Social */
  hasVisitors: boolean;
  recentActivityLevel: number; // 0-1: how much happened recently
}

/** Encode room features into a fixed-dim vector */
export function encodeRoomFeatures(room: RoomFeatures): Float32Array {
  const v = zeros(ROOM_DIM);
  v[0]  = room.warmth;
  v[1]  = room.energy;
  v[2]  = room.mystery;
  v[3]  = room.nature;
  v[4]  = room.noise;
  v[5]  = room.seatingCount / 10;
  v[6]  = room.lightingCount / 10;
  v[7]  = room.decorCount / 10;
  v[8]  = room.storageCount / 10;
  v[9]  = room.natureCount / 10;
  v[10] = room.entertainmentCount / 10;
  v[11] = room.furnitureDensity;
  v[12] = room.symmetry;
  v[13] = room.centeredness;
  v[14] = room.timeOfDay;
  v[15] = room.seasonPhase;
  v[16] = room.hasVisitors ? 1 : 0;
  v[17] = room.recentActivityLevel;
  // Derived features
  v[18] = (room.warmth + room.nature) / 2;           // "coziness"
  v[19] = (room.energy + room.noise) / 2;             // "liveliness"
  v[20] = (room.mystery + (1 - room.noise)) / 2;      // "contemplative"
  v[21] = room.seatingCount / Math.max(1, room.furnitureDensity * 10); // "social readiness"
  v[22] = room.lightingCount / Math.max(1, room.furnitureDensity * 10); // "illumination ratio"
  // Cross-features
  v[23] = room.warmth * room.nature;                   // "garden warmth"
  v[24] = room.energy * room.entertainmentCount / 10;  // "fun factor"
  v[25] = (1 - room.energy) * room.mystery;            // "stillness depth"
  v[26] = room.symmetry * room.centeredness;            // "ordered"
  v[27] = (1 - room.symmetry) * room.mystery;          // "organic chaos"
  v[28] = Math.sin(room.timeOfDay * Math.PI * 2);      // cyclical time encoding
  v[29] = Math.cos(room.timeOfDay * Math.PI * 2);
  v[30] = Math.sin(room.seasonPhase * Math.PI * 2);
  v[31] = Math.cos(room.seasonPhase * Math.PI * 2);
  return v;
}

// ─── Cross-Modal Binder ──────────────────────────────────────────────────────

/** All 5 modality keys */
export type Modality = "text" | "room" | "affect" | "personality" | "drives";

/** Configuration for each modality's projection head */
interface ModalityHead {
  inputDim: number;
  W: Float32Array;
  b: Float32Array;
}

export class CrossModalBinder {
  /** Projection heads for each modality → SHARED_DIM */
  private heads: Record<Modality, ModalityHead>;

  /** Bilinear interaction matrix: SHARED_DIM × SHARED_DIM */
  private W_bilinear: Float32Array;

  /** Running coherence history for trend detection */
  private coherenceHistory: number[] = [];
  private maxHistory = 50;

  /** Positive/negative pair buffer for online contrastive learning */
  private positives: Array<{ text: Float32Array; room: Float32Array }> = [];
  /** Multi-modal pair buffer for 5-way contrastive */
  private multiPairs: Array<Partial<Record<Modality, Float32Array>>> = [];
  private maxPairs = 200;

  constructor(seed = 5555) {
    const biScale = 1 / Math.sqrt(SHARED_DIM);

    // Create projection head for each modality
    const dims: Record<Modality, number> = {
      text: TEXT_DIM,
      room: ROOM_DIM,
      affect: AFFECT_DIM,
      personality: PERSONALITY_DIM,
      drives: DRIVE_DIM,
    };

    this.heads = {} as Record<Modality, ModalityHead>;
    let seedOffset = 0;
    for (const [mod, dim] of Object.entries(dims) as Array<[Modality, number]>) {
      const scale = 1 / Math.sqrt(dim);
      this.heads[mod] = {
        inputDim: dim,
        W: randomMatrix(SHARED_DIM, dim, scale, seed + seedOffset),
        b: zeros(SHARED_DIM),
      };
      seedOffset += 10;
    }

    this.W_bilinear = randomMatrix(SHARED_DIM, SHARED_DIM, biScale, seed + 100);
  }

  /** Get the W and b for legacy text/room accessors */
  private get W_text() { return this.heads.text.W; }
  private get b_text() { return this.heads.text.b; }
  private get W_room() { return this.heads.room.W; }
  private get b_room() { return this.heads.room.b; }

  /** Project any modality into shared space */
  bind(modality: Modality, vec: Float32Array): Float32Array {
    const head = this.heads[modality];
    const proj = matvec(head.W, SHARED_DIM, head.inputDim, vec);
    vadd(proj, head.b, proj);
    for (let i = 0; i < SHARED_DIM; i++) proj[i] = Math.tanh(proj[i]);
    return normalize(proj);
  }

  /** Project text embedding into shared space */
  bindText(textEmbed: Float32Array): Float32Array {
    return this.bind("text", textEmbed);
  }

  /** Project room features into shared space */
  bindRoom(roomVec: Float32Array): Float32Array {
    return this.bind("room", roomVec);
  }

  /** Project affect vector into shared space */
  bindAffect(affect: Float32Array): Float32Array {
    return this.bind("affect", affect);
  }

  /** Project personality vector into shared space */
  bindPersonality(personality: Float32Array): Float32Array {
    return this.bind("personality", personality);
  }

  /** Project drive vector into shared space */
  bindDrives(drives: Float32Array): Float32Array {
    return this.bind("drives", drives);
  }

  /**
   * Coherence: how aligned are the player's words and the room right now?
   *
   * 1.0 = player talking exactly about what's in the room
   * 0.0 = complete mismatch
   *
   * Uses bilinear attention for richer interaction than pure cosine.
   */
  coherence(textEmbed: Float32Array, roomVec: Float32Array): number {
    const tProj = this.bindText(textEmbed);
    const rProj = this.bindRoom(roomVec);

    // Bilinear score: t^T · W · r
    const Wr = matvec(this.W_bilinear, SHARED_DIM, SHARED_DIM, rProj);
    const bilinearScore = dot(tProj, Wr);

    // Also compute pure cosine for stability
    const cosineScore = cosine(tProj, rProj);

    // Blend: 60% bilinear (richer), 40% cosine (stable)
    const raw = bilinearScore * 0.6 + cosineScore * 0.4;

    // Sigmoid to [0, 1]
    const score = 1 / (1 + Math.exp(-raw * 3));

    // Track history
    this.coherenceHistory.push(score);
    if (this.coherenceHistory.length > this.maxHistory) {
      this.coherenceHistory.shift();
    }

    return score;
  }

  /**
   * Incoherence: the inverse — how MISMATCHED are words and room?
   * High incoherence is interesting: the player might be yearning
   * for something their room doesn't provide.
   */
  incoherence(textEmbed: Float32Array, roomVec: Float32Array): number {
    return 1 - this.coherence(textEmbed, roomVec);
  }

  /**
   * Coherence drift: is the alignment between words and room changing?
   * Positive = becoming more aligned (player settling in)
   * Negative = becoming less aligned (player yearning, restless)
   */
  coherenceDrift(): number {
    const h = this.coherenceHistory;
    if (h.length < 6) return 0;
    const recent = h.slice(-3).reduce((s, v) => s + v, 0) / 3;
    const older = h.slice(-6, -3).reduce((s, v) => s + v, 0) / 3;
    return recent - older;
  }

  /**
   * Semantic gap analysis: what dimension of the shared space
   * shows the most disagreement between text and room?
   *
   * Returns the top-K dimensions with largest gap, which can be
   * mapped back to interpretable concepts.
   */
  gapAnalysis(textEmbed: Float32Array, roomVec: Float32Array, topK = 3): Array<{ dim: number; gap: number; direction: "text_wants" | "room_has" }> {
    const tProj = this.bindText(textEmbed);
    const rProj = this.bindRoom(roomVec);

    const gaps: Array<{ dim: number; gap: number; direction: "text_wants" | "room_has" }> = [];
    for (let i = 0; i < SHARED_DIM; i++) {
      const diff = tProj[i] - rProj[i];
      gaps.push({
        dim: i,
        gap: Math.abs(diff),
        direction: diff > 0 ? "text_wants" : "room_has",
      });
    }

    gaps.sort((a, b) => b.gap - a.gap);
    return gaps.slice(0, topK);
  }

  // ─── Online Contrastive Learning ───────────────────────────────────────

  /**
   * Compute cross-modal coherence between any two modalities.
   * Returns 0-1 cosine similarity in the shared space.
   */
  crossCoherence(modA: Modality, vecA: Float32Array, modB: Modality, vecB: Float32Array): number {
    const projA = this.bind(modA, vecA);
    const projB = this.bind(modB, vecB);
    return (cosine(projA, projB) + 1) / 2; // shift from [-1,1] to [0,1]
  }

  /**
   * Record a positive pair: this text was said while looking at this room.
   * Used for online contrastive weight updates.
   */
  recordPair(textEmbed: Float32Array, roomVec: Float32Array): void {
    this.positives.push({
      text: new Float32Array(textEmbed),
      room: new Float32Array(roomVec),
    });
    if (this.positives.length > this.maxPairs) {
      this.positives.shift();
    }
  }

  /**
   * Record a multi-modal snapshot: all present modalities at this moment.
   * Used for 5-way contrastive learning — all present pairs should align.
   */
  recordMultiPair(snapshot: Partial<Record<Modality, Float32Array>>): void {
    const copy: Partial<Record<Modality, Float32Array>> = {};
    for (const [k, v] of Object.entries(snapshot) as Array<[Modality, Float32Array]>) {
      copy[k] = new Float32Array(v);
    }
    this.multiPairs.push(copy);
    if (this.multiPairs.length > this.maxPairs) {
      this.multiPairs.shift();
    }
  }

  /**
   * Run one step of online contrastive learning.
   * Positive pairs should have high coherence.
   * Random negative pairs (shuffled) should have low coherence.
   *
   * InfoNCE loss: -log(exp(sim(t,r)/τ) / Σexp(sim(t,r_j)/τ))
   */
  learnStep(): number {
    if (this.positives.length < 10) return 0;

    let totalLoss = 0;
    const batchSize = Math.min(8, this.positives.length);

    for (let b = 0; b < batchSize; b++) {
      const idx = Math.floor(Math.random() * this.positives.length);
      const pair = this.positives[idx];

      const tProj = this.bindText(pair.text);
      const rProj = this.bindRoom(pair.room);

      // Positive score
      const posSim = dot(tProj, rProj) / TEMPERATURE;

      // Negative scores: compare against random other rooms
      let logSumExp = posSim;
      const negCount = Math.min(5, this.positives.length - 1);
      for (let n = 0; n < negCount; n++) {
        let negIdx = Math.floor(Math.random() * this.positives.length);
        if (negIdx === idx) negIdx = (negIdx + 1) % this.positives.length;
        const negRoom = this.bindRoom(this.positives[negIdx].room);
        const negSim = dot(tProj, negRoom) / TEMPERATURE;
        logSumExp = logSumExpAdd(logSumExp, negSim);
      }

      const loss = -posSim + logSumExp;
      totalLoss += loss;

      // Gradient step on W_text and W_room
      // Simplified: push positive pairs closer, negative pairs apart
      // Gradient clipping prevents explosion from low τ (0.07)
      const GRAD_CLIP = 1.0;
      const WDECAY = 1 - 1e-5;
      for (let i = 0; i < SHARED_DIM; i++) {
        let grad = (tProj[i] - rProj[i]) * LEARN_RATE;
        grad = Math.max(-GRAD_CLIP, Math.min(GRAD_CLIP, grad));
        // Adjust text projection to move toward room (with decay)
        for (let j = 0; j < TEXT_DIM; j++) {
          this.W_text[i * TEXT_DIM + j] = this.W_text[i * TEXT_DIM + j] * WDECAY - grad * pair.text[j];
        }
        // Adjust room projection to move toward text (with decay)
        for (let j = 0; j < ROOM_DIM; j++) {
          this.W_room[i * ROOM_DIM + j] = this.W_room[i * ROOM_DIM + j] * WDECAY - grad * pair.room[j];
        }
      }
    }

    return totalLoss / batchSize;
  }

  /**
   * Run one step of multi-modal contrastive learning.
   * For each snapshot, all present modality pairs should be close;
   * shuffled negatives should be far.
   */
  learnMultiStep(): number {
    if (this.multiPairs.length < 10) return 0;

    let totalLoss = 0;
    const batchSize = Math.min(8, this.multiPairs.length);
    const modalities: Modality[] = ["text", "room", "affect", "personality", "drives"];

    for (let b = 0; b < batchSize; b++) {
      const idx = Math.floor(Math.random() * this.multiPairs.length);
      const snapshot = this.multiPairs[idx];
      const present = modalities.filter(m => snapshot[m] !== undefined);
      if (present.length < 2) continue;

      // For each pair of present modalities
      for (let i = 0; i < present.length; i++) {
        for (let j = i + 1; j < present.length; j++) {
          const modA = present[i];
          const modB = present[j];
          const projA = this.bind(modA, snapshot[modA]!);
          const projB = this.bind(modB, snapshot[modB]!);

          // Positive: these should be close
          const posSim = dot(projA, projB) / TEMPERATURE;

          // Negative: random other snapshot's modB
          let negIdx = Math.floor(Math.random() * this.multiPairs.length);
          if (negIdx === idx) negIdx = (negIdx + 1) % this.multiPairs.length;
          const negSnap = this.multiPairs[negIdx];
          let negSim = posSim;
          if (negSnap[modB]) {
            const projNeg = this.bind(modB, negSnap[modB]!);
            negSim = dot(projA, projNeg) / TEMPERATURE;
          }

          const loss = -posSim + logSumExpAdd(posSim, negSim);
          totalLoss += loss;

          // Gradient step: push positive pair closer
          const GRAD_CLIP = 1.0;
          const WDECAY = 1 - 1e-5;
          const headA = this.heads[modA];
          const headB = this.heads[modB];
          for (let d = 0; d < SHARED_DIM; d++) {
            let grad = (projA[d] - projB[d]) * LEARN_RATE * 0.5; // half rate for multi-pair
            grad = Math.max(-GRAD_CLIP, Math.min(GRAD_CLIP, grad));
            for (let k = 0; k < headA.inputDim; k++) {
              headA.W[d * headA.inputDim + k] = headA.W[d * headA.inputDim + k] * WDECAY - grad * snapshot[modA]![k];
            }
            for (let k = 0; k < headB.inputDim; k++) {
              headB.W[d * headB.inputDim + k] = headB.W[d * headB.inputDim + k] * WDECAY + grad * snapshot[modB]![k];
            }
          }
        }
      }
    }

    return batchSize > 0 ? totalLoss / batchSize : 0;
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    const data: Record<string, string> = {
      W_bilinear: f32ToBase64(this.W_bilinear),
    };
    for (const [mod, head] of Object.entries(this.heads)) {
      data[`W_${mod}`] = f32ToBase64(head.W);
      data[`b_${mod}`] = f32ToBase64(head.b);
    }
    return JSON.stringify(data);
  }

  loadWeights(json: string): void {
    const d = JSON.parse(json);
    // Legacy support: old format had W_text, b_text, W_room, b_room
    for (const [mod, head] of Object.entries(this.heads) as Array<[string, ModalityHead]>) {
      const wKey = `W_${mod}`;
      const bKey = `b_${mod}`;
      if (d[wKey]) head.W = typeof d[wKey] === "string" ? base64ToF32(d[wKey]) : new Float32Array(d[wKey]);
      if (d[bKey]) head.b = typeof d[bKey] === "string" ? base64ToF32(d[bKey]) : new Float32Array(d[bKey]);
    }
    if (d.W_bilinear) this.W_bilinear = typeof d.W_bilinear === "string" ? base64ToF32(d.W_bilinear) : new Float32Array(d.W_bilinear);
  }
}

/** Numerically stable log-sum-exp addition */
function logSumExpAdd(a: number, b: number): number {
  const max = Math.max(a, b);
  return max + Math.log(Math.exp(a - max) + Math.exp(b - max));
}

// ─── Furniture → Emotion Semantic Mapping ────────────────────────────────────
// What does the player's room *say* about them?
// This is not interpretation — it's geometric projection from
// room features to emotional/personality space.

export interface RoomNarrative {
  /** What personality does this room suggest? */
  impliedPersonality: {
    introverted: number;  // high seating + low noise + nature
    creative: number;     // high decor + asymmetry + mystery
    social: number;       // high seating + entertainment + visitors
    contemplative: number; // low energy + mystery + nature
    playful: number;      // high energy + entertainment + chaos
  };
  /** What emotional atmosphere does the room create? */
  atmosphere: {
    safe: number;         // warmth + lighting + low noise
    stimulating: number;  // energy + entertainment + visitors
    peaceful: number;     // nature + warmth + low energy
    lonely: number;       // no visitors + sparse furniture + quiet
    cozy: number;         // warmth + seating + lighting + dense furniture
  };
  /** What is the room "missing"? (gaps that might explain yearning) */
  gaps: string[];
  /** One-line room reading */
  reading: string;
}

/** Read the room as a narrative about its inhabitant */
export function readRoomNarrative(room: RoomFeatures): RoomNarrative {
  const introverted = (room.nature + (1 - room.noise) + (1 - room.energy)) / 3;
  const creative = (room.decorCount / 10 + (1 - room.symmetry) + room.mystery) / 3;
  const social = (room.seatingCount / 10 + room.entertainmentCount / 10 + (room.hasVisitors ? 1 : 0)) / 3;
  const contemplative = ((1 - room.energy) + room.mystery + room.nature) / 3;
  const playful = (room.energy + room.entertainmentCount / 10 + (1 - room.symmetry)) / 3;

  const safe = (room.warmth + room.lightingCount / 10 + (1 - room.noise)) / 3;
  const stimulating = (room.energy + room.entertainmentCount / 10 + (room.hasVisitors ? 0.5 : 0)) / 2.5;
  const peaceful = (room.nature + room.warmth + (1 - room.energy)) / 3;
  const lonely = ((1 - (room.hasVisitors ? 1 : 0)) + (1 - room.furnitureDensity) + (1 - room.noise)) / 3;
  const cozy = (room.warmth + room.seatingCount / 10 + room.lightingCount / 10 + room.furnitureDensity) / 4;

  // Gap analysis: what's low that might create yearning?
  const gaps: string[] = [];
  if (room.warmth < 0.2) gaps.push("warmth");
  if (room.nature < 0.15) gaps.push("nature/greenery");
  if (room.lightingCount < 2) gaps.push("lighting");
  if (room.seatingCount < 1) gaps.push("places to sit");
  if (social < 0.15) gaps.push("social elements");
  if (room.decorCount < 1) gaps.push("personal decoration");

  // One-line reading
  let reading = "a room";
  const traits: string[] = [];
  if (cozy > 0.6) traits.push("cozy");
  if (peaceful > 0.6) traits.push("peaceful");
  if (creative > 0.5) traits.push("creative");
  if (stimulating > 0.5) traits.push("lively");
  if (lonely > 0.6) traits.push("solitary");
  if (safe > 0.6) traits.push("comforting");
  if (introverted > 0.6) traits.push("introspective");

  if (traits.length > 0) {
    reading = `a ${traits.slice(0, 3).join(", ")} space`;
  }
  if (gaps.length > 0) {
    reading += ` (missing: ${gaps.slice(0, 2).join(", ")})`;
  }

  return {
    impliedPersonality: { introverted, creative, social, contemplative, playful },
    atmosphere: { safe, stimulating, peaceful, lonely, cozy },
    gaps,
    reading,
  };
}

// ─── Room-Personality Alignment ──────────────────────────────────────────────
// Does the player's room match their conversational personality?
// A deeply curious person living in a barren room = interesting tension.

export interface RoomPersonalityAlignment {
  /** Overall alignment (0=mismatch, 1=aligned) */
  alignment: number;
  /** Specific mismatches */
  mismatches: Array<{ dimension: string; roomSays: number; playerIs: number; tension: number }>;
  /** Context string for EntityZero */
  insight: string;
}

/**
 * Compare room-implied personality with actual personality from Liquid ODE.
 */
export function alignRoomPersonality(
  room: RoomFeatures,
  personality: { patience: number; curiosity: number; warmth: number; wit: number },
): RoomPersonalityAlignment {
  const narrative = readRoomNarrative(room);
  const ip = narrative.impliedPersonality;

  // Map room personality to same axes as Liquid personality
  const roomPatience = (ip.contemplative + ip.introverted) / 2;
  const roomCuriosity = (ip.creative + narrative.atmosphere.stimulating) / 2;
  const roomWarmth = (narrative.atmosphere.cozy + narrative.atmosphere.safe) / 2;
  const roomWit = (ip.playful + narrative.atmosphere.stimulating) / 2;

  const dims = [
    { dimension: "patience", roomSays: roomPatience, playerIs: personality.patience },
    { dimension: "curiosity", roomSays: roomCuriosity, playerIs: personality.curiosity },
    { dimension: "warmth", roomSays: roomWarmth, playerIs: personality.warmth },
    { dimension: "wit", roomSays: roomWit, playerIs: personality.wit },
  ];

  const mismatches: RoomPersonalityAlignment["mismatches"] = [];
  let totalAlignment = 0;

  for (const d of dims) {
    const tension = Math.abs(d.roomSays - d.playerIs);
    totalAlignment += 1 - tension;
    if (tension > 0.3) {
      mismatches.push({ ...d, tension });
    }
  }
  totalAlignment /= dims.length;

  // Generate insight
  let insight = "";
  if (mismatches.length === 0) {
    insight = "Room matches personality well";
  } else {
    const biggest = mismatches.sort((a, b) => b.tension - a.tension)[0];
    if (biggest.roomSays > biggest.playerIs) {
      insight = `Room suggests more ${biggest.dimension} than conversation shows`;
    } else {
      insight = `Conversation shows more ${biggest.dimension} than room suggests`;
    }
  }

  return { alignment: totalAlignment, mismatches, insight };
}
